/**
 * TransferContactModal - Transfere o contato para um membro humano da equipe
 * ou para o agente de IA. Espelha a linguagem visual do LabelPickerModal
 * (overlay, container, header) para o menu do chat ficar coeso.
 *
 * A mudança vira o "responsável do contato" (assigned_to) com auditoria —
 * alimenta a tag do chat list, o drawer e o histórico de transferências.
 *
 * Transferir para OUTRO membro humano passa por um passo de confirmação com
 * o aviso do motor de propriedade (cartão + histórico vão para o receptor;
 * a thread original fica registrada no workspace do administrador).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { X, UserCheck, Check, Loader2, Bot, User, AlertTriangle } from 'lucide-react';
import assignmentService from '../../services/global/assignmentService';
import { useAuth } from '../../contexts/AuthContext';

const TransferContactModal = ({
  isOpen,
  onClose,
  contactPhone,
  contactName,
  colors,
  onChanged,            // (assignedTo|null) => void
  displayMode = 'modal',
}) => {
  const { user } = useAuth();
  const [targets, setTargets] = useState([]);
  const [current, setCurrent] = useState(null);
  const [origin, setOrigin] = useState(null);
  const [pendingTarget, setPendingTarget] = useState(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !contactPhone) return;
    // O modal fica montado entre usos (isOpen alterna) — reset COMPLETO ao
    // abrir, padrão do AgentToggleModal, senão o passo de confirmação da
    // utilização anterior reaparece.
    let cancelled = false;
    setTargets([]);
    setCurrent(null);
    setOrigin(null);
    setPendingTarget(null);
    setBusyId(null);
    setLoading(true);
    setError(null);
    setReason('');

    Promise.all([
      assignmentService.getTargets(),
      assignmentService.get(contactPhone),
    ])
      .then(([targetsRes, currentRes]) => {
        if (cancelled) return;
        setTargets(targetsRes?.data || []);
        setCurrent(currentRes?.data?.assigned_to || null);
        setOrigin(currentRes?.data?.origin || null);
      })
      .catch((err) => {
        if (!cancelled) {
          setError('Não foi possível carregar a equipe.');
          console.error(err);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [isOpen, contactPhone]);

  const grouped = useMemo(() => {
    const humanos = targets.filter((t) => t.type === 'member');
    const agentes = targets.filter((t) => t.type === 'agent');
    const out = [];
    if (humanos.length) out.push({ key: 'humanos', title: 'Equipe', items: humanos });
    if (agentes.length) out.push({ key: 'agentes', title: 'Agentes de IA', items: agentes });
    return out;
  }, [targets]);

  // Alvo membro que NÃO é o próprio usuário: 2 passos — mostra o aviso de
  // transferência e só executa no "Confirmar transferência". Agentes e o
  // próprio usuário mantêm o fluxo de 1 clique; clicar em outro alvo
  // troca/reseta o passo.
  const handleSelect = (target) => {
    if (busyId) return;
    if (target.type === 'member' && target.id !== user?.id) {
      setError(null);
      setPendingTarget(target);
      return;
    }
    setPendingTarget(null);
    handleAssign(target);
  };

  const handleAssign = async (target) => {
    if (busyId) return;
    setBusyId(target.id);
    setError(null);
    try {
      const res = await assignmentService.assign(contactPhone, target, reason.trim());
      const assigned = res?.data?.assigned_to || null;
      setCurrent(assigned);
      setPendingTarget(null);
      if (typeof onChanged === 'function') onChanged(assigned);
    } catch (err) {
      setError('Erro ao transferir. Tente novamente.');
      console.error(err);
    } finally {
      setBusyId(null);
    }
  };

  const handleClear = async () => {
    if (busyId) return;
    setBusyId('__clear__');
    setError(null);
    try {
      await assignmentService.clear(contactPhone);
      setCurrent(null);
      if (typeof onChanged === 'function') onChanged(null);
    } catch (err) {
      setError('Erro ao remover responsável.');
      console.error(err);
    } finally {
      setBusyId(null);
    }
  };

  if (!isOpen) return null;

  const isDrawer = displayMode === 'drawer';

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
        display: 'flex',
        alignItems: isDrawer ? 'stretch' : 'center',
        justifyContent: isDrawer ? 'flex-end' : 'center',
        padding: isDrawer ? 0 : '20px',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: colors.bgPrimary,
          borderRadius: isDrawer ? 0 : '16px',
          width: isDrawer ? 420 : '100%',
          maxWidth: isDrawer ? '100%' : '440px',
          overflow: 'hidden',
          boxShadow: isDrawer
            ? '-8px 0 32px rgba(0,0,0,0.25)'
            : '0 20px 60px rgba(0,0,0,0.3)',
          height: isDrawer ? '100%' : 'auto',
          maxHeight: isDrawer ? '100%' : '85vh',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: `1px solid ${colors.borderColor}`,
          flexShrink: 0,
        }}>
          <div>
            <h3 style={{
              margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary,
              display: 'flex', alignItems: 'center', gap: '8px',
            }}>
              <UserCheck size={18} color="#f59e0b" /> Transferir contato
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: colors.textSecondary }}>
              Para: {contactName || contactPhone}
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary,
            padding: '8px', borderRadius: '8px',
          }}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{
          padding: '16px 20px', display: 'flex', flexDirection: 'column',
          gap: '14px', overflowY: 'auto', flex: 1,
        }}>
          {/* Responsável atual */}
          {current && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <span style={{ fontSize: '12px', fontWeight: 500, color: colors.textSecondary }}>
                Responsável atual
              </span>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                backgroundColor: 'rgba(245,158,11,0.15)', color: '#b45309',
                fontSize: '12px', fontWeight: 600, padding: '4px 10px',
                borderRadius: '999px', lineHeight: '16px', alignSelf: 'flex-start',
              }}>
                {current.type === 'agent' ? <Bot size={12} /> : <User size={12} />}
                {current.name}
                <button
                  onClick={handleClear}
                  disabled={busyId === '__clear__'}
                  style={{
                    display: 'inline-flex', alignItems: 'center',
                    background: 'transparent', border: 'none', color: 'inherit',
                    cursor: 'pointer', padding: 0,
                    opacity: busyId === '__clear__' ? 0.5 : 1,
                  }}
                  title="Remover responsável"
                >
                  {busyId === '__clear__' ? (
                    <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <X size={12} />
                  )}
                </button>
              </span>
            </div>
          )}

          {/* Motivo (opcional) */}
          <div style={{
            display: 'flex', alignItems: 'center',
            backgroundColor: colors.bgSecondary, borderRadius: '8px',
            padding: '0 12px', border: `1px solid ${colors.borderColor}`,
          }}>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Motivo da transferência (opcional)"
              maxLength={200}
              style={{
                flex: 1, padding: '10px 0', border: 'none', background: 'transparent',
                fontSize: '13px', color: colors.textPrimary, outline: 'none',
              }}
            />
          </div>

          {/* Lista de destinos */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {loading ? (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '24px 0', color: colors.textTertiary,
              }}>
                <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            ) : grouped.length === 0 ? (
              <span style={{ fontSize: '13px', color: colors.textTertiary, padding: '8px 4px' }}>
                Nenhum destino disponível.
              </span>
            ) : (
              grouped.map(({ key, title, items }) => (
                <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: colors.textTertiary,
                    textTransform: 'uppercase', letterSpacing: '0.4px',
                    padding: '6px 4px 2px',
                  }}>
                    {title}
                  </span>
                  {items.map((target) => {
                    const isCurrent =
                      current && current.id === target.id && current.type === target.type;
                    const isBusy = busyId === target.id;
                    const isPending =
                      pendingTarget &&
                      pendingTarget.id === target.id &&
                      pendingTarget.type === target.type;
                    // Re-transferência para a dona física da conexão sem
                    // espelho no admin: a thread já vive no workspace dela —
                    // a linha do administrador não se aplica.
                    const showAdminLine = !(
                      origin && origin.user_id === target.id && !origin.has_admin_mirror
                    );
                    return (
                      <React.Fragment key={`${target.type}:${target.id}`}>
                      <button
                        onClick={() => !isCurrent && handleSelect(target)}
                        disabled={isBusy || isCurrent}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          width: '100%', padding: '10px 12px',
                          background: isCurrent ? 'rgba(245,158,11,0.10)' : 'transparent',
                          border: 'none', borderRadius: '8px',
                          cursor: isBusy ? 'wait' : isCurrent ? 'default' : 'pointer',
                          textAlign: 'left',
                          transition: 'background-color 0.15s',
                        }}
                        onMouseEnter={(e) => {
                          if (!isCurrent) e.currentTarget.style.backgroundColor = colors.bgSecondary;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = isCurrent
                            ? 'rgba(245,158,11,0.10)' : 'transparent';
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{
                            width: 28, height: 28, borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            backgroundColor: target.type === 'agent'
                              ? `${colors.accentPrimary}22`
                              : 'rgba(245,158,11,0.15)',
                            color: target.type === 'agent' ? colors.accentPrimary : '#b45309',
                            flexShrink: 0,
                          }}>
                            {target.type === 'agent' ? <Bot size={14} /> : <User size={14} />}
                          </span>
                          <span style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '14px', color: colors.textPrimary }}>
                              {target.name}
                            </span>
                            {(target.role || target.category) && (
                              <span style={{ fontSize: '11px', color: colors.textTertiary }}>
                                {[
                                  target.role
                                    ? (target.role === 'admin' ? 'Administrador' : 'Membro')
                                    : null,
                                  target.category?.title || null,
                                ].filter(Boolean).join(' · ')}
                              </span>
                            )}
                          </span>
                        </span>
                        {isBusy ? (
                          <Loader2 size={16} color={colors.textSecondary} style={{ animation: 'spin 1s linear infinite' }} />
                        ) : isCurrent ? (
                          <Check size={16} color="#f59e0b" />
                        ) : null}
                      </button>
                      {isPending && (
                        <div style={{
                          display: 'flex', flexDirection: 'column', gap: 10,
                          margin: '2px 0 6px', padding: '10px 12px',
                          backgroundColor: 'rgba(245,158,11,0.10)',
                          border: '1px solid rgba(245,158,11,0.4)',
                          borderRadius: 8,
                        }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                            <AlertTriangle size={16} color="#b45309" style={{ flexShrink: 0, marginTop: 1 }} />
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <span style={{ fontSize: '12.5px', lineHeight: '18px', color: colors.textPrimary }}>
                                {target.name} recebe o contato com todo o histórico
                                e atenderá pelo número dele.
                                {showAdminLine &&
                                  ' A conversa atual também fica registrada no workspace do administrador.'}
                              </span>
                              {target.has_channel === false && (
                                <span style={{ fontSize: '12.5px', lineHeight: '18px', color: '#b45309', fontWeight: 500 }}>
                                  Este membro ainda não conectou um número — o
                                  contato ficará aguardando a conexão.
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleAssign(target)}
                            disabled={isBusy}
                            style={{
                              alignSelf: 'flex-end', display: 'inline-flex',
                              alignItems: 'center', gap: 6, padding: '7px 14px',
                              backgroundColor: '#f59e0b', color: 'white',
                              border: 'none', borderRadius: 8,
                              fontSize: '12.5px', fontWeight: 600,
                              cursor: isBusy ? 'wait' : 'pointer',
                              opacity: isBusy ? 0.7 : 1,
                            }}
                          >
                            {isBusy && (
                              <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
                            )}
                            Confirmar transferência
                          </button>
                        </div>
                      )}
                      </React.Fragment>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {error && (
            <span style={{ fontSize: '12px', color: '#ef4444' }}>{error}</span>
          )}
        </div>

        {/* Footer — a transferência já é aplicada ao escolher o destino (o passo
            de confirmar entre membros continua como aviso de propriedade). O botão
            é só "Fechar" neutro, não um "salvar" (que era o "Concluído" confuso). */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 20px', borderTop: `1px solid ${colors.borderColor}`,
          flexShrink: 0, gap: 12,
        }}>
          <span style={{ fontSize: '12px', color: colors.textTertiary }}>
            A transferência é aplicada ao escolher.
          </span>
          <button
            onClick={onClose}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0,
              padding: '8px 18px', backgroundColor: 'transparent', color: colors.textSecondary,
              border: `1px solid ${colors.borderColor}`, borderRadius: '8px',
              fontSize: '13px', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransferContactModal;
